import { Platform } from "react-native"

import * as Sentry from "@sentry/react-native"
import { AxiosRequestConfig } from "axios"

/**
 * Extracts safe-to-log information from an API request.
 * Separates the URL path from query params to avoid logging sensitive data
 * that may be present in query parameters (tokens, credentials, etc.).
 *
 * @param url - The request URL
 * @returns Object with urlPath and hasQueryParams flag
 */
export const getSafeRequestInfo = (requestConfig: AxiosRequestConfig) => {
  return {
    method: requestConfig.method,
    baseURL: requestConfig.baseURL,
    urlPath: requestConfig.url,
    hasQueryParams: !!requestConfig.params,
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

/**
 * Extracts safe-to-log fields from a websocket error payload.
 * Avoids logging raw message data that could contain conversation content.
 */
export const getSafeWebsocketErrorInfo = (message: {
  type?: string
  parsedData?: unknown
  status_code?: number
}) => {
  const parsedData = isRecord(message.parsedData) ? message.parsedData : undefined

  return {
    type: message.type,
    code: parsedData && "code" in parsedData ? parsedData.code : undefined,
    result: parsedData && "result" in parsedData ? parsedData.result : undefined,
    status_code: message.status_code,
  }
}

/**
 * Error classifications used to sort errors on error reporting services.
 */
export enum ErrorType {
  /**
   * An error that would normally cause a red screen in dev
   * and force the user to sign out and restart.
   */
  FATAL = "Fatal",
  /**
   * An error caught by try/catch where defined using Reactotron.tron.error.
   */
  HANDLED = "Handled",
}

/**
 * Manually report a handled error.
 */
export const reportCrash = (error: Error, type: ErrorType = ErrorType.FATAL) => {
  if (Platform.OS === "web") {
    console.error(error)
    return
  }
  if (__DEV__) {
    // Log to console and Reactotron in development
    const message = error.message || "Unknown"
    console.error(error)
    console.log(message, type)
  } else {
    Sentry.captureException(error)
  }
}

// Map Sentry severities to available console log functions
const getLogFunction = (logLevel: Sentry.SeverityLevel) => {
  switch (logLevel) {
    case "error":
      return console.error
    case "fatal":
      return console.error
    case "warning":
      return console.warn
    case "info":
      return console.log
    case "log":
      return console.log
    case "debug":
      return console.debug
    default:
      return console.warn
  }
}

export const logSentryBreadcrumb = (message: string) => {
  // FIXME: we should support different levels for breadcrumbs, as in logSentryEvent
  const logLevel: Sentry.SeverityLevel = "info"

  if (__DEV__ || Platform.OS === "web") {
    // Log to console in development
    const logFn = getLogFunction(logLevel)
    logFn(`[logSentryBreadcrumb]:${logLevel}`, message)
  } else {
    Sentry.addBreadcrumb({
      category: "log",
      message,
      level: logLevel,
    })
  }
}

/**
 * Log a handled event to Sentry. Use this for interesting events that
 * we'd like to track independently of unhandled crashes.
 *
 * This function also creates a breadcrumb for the event, so there's no need
 * to call logSentryBreadcrumb separately when using this function. This allows
 * linking to breadcrumbs for a span that didn't generate a crash report.
 */
export const logSentryEvent = (
  logLevel: Sentry.SeverityLevel,
  message: string,
  data?: Record<string, unknown> | Error,
) => {
  if (__DEV__ || Platform.OS === "web") {
    // Log to console in development
    const logFn = getLogFunction(logLevel)
    logFn(`[logSentryEvent]:${logLevel}`, message, data)
  } else {
    // Add a breadcrumb so future errors see this event in their trail
    Sentry.addBreadcrumb({
      category: "event",
      message,
      level: logLevel,
      data: data instanceof Error ? { error: data.message } : data,
    })

    if (data instanceof Error) {
      Sentry.captureException(data, {
        level: logLevel,
        extra: { message },
      })
    } else {
      Sentry.captureMessage(message, {
        level: logLevel,
        extra: data,
      })
    }
  }
}
